import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { useCurrentUnit } from './useCurrentUnit.js'

// Drives the Receive Inventory screen.
// Mirrors useWasteData's shape so the two screens have parallel APIs.
//
// Returns: items, suppliers, todayLogs, todayTotal, loading, refresh, deleteLog.
//
// Items are also fetched here so the entry form can do typeahead. We
// could centralize at App level later; for now it's fine to fetch
// twice (only the active tab's hook is mounted at any moment).
export function useReceiveData() {
  const [items, setItems] = useState([])
  const [suppliers, setSuppliers] = useState([])
  const [todayLogs, setTodayLogs] = useState([])
  // 90-day receive history — feeds the AI anomaly scan modal. Loaded
  // alongside today's logs so the scan modal fires without an extra
  // round-trip when the manager taps the button.
  const [recentReceives, setRecentReceives] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Multi-unit scope — null for single-unit (queries byte-identical).
  const { currentRestaurantId } = useCurrentUnit()

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    // Apply the unit filter only when grouped; single-unit (null) is
    // untouched and keeps relying on RLS membership scoping.
    const scope = (q) => (currentRestaurantId ? q.eq('restaurant_id', currentRestaurantId) : q)

    // Kitchen-day boundary — computed fresh per fetch so a tab left
    // open across midnight rolls forward correctly. Same convention
    // as useWasteData; see comments there.
    const now = new Date()
    const startOfToday = new Date(
      now.getFullYear(), now.getMonth(), now.getDate(),
      0, 0, 0, 0
    ).toISOString()

    // 90-day window for the AI anomaly scan. We pull this alongside
    // today's logs so the modal fires its API call without another
    // round-trip.
    const ninetyDaysAgo = new Date()
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90)
    const since90Ts = ninetyDaysAgo.toISOString()

    try {
      const [itemsRes, suppliersRes, logsRes, recentRes] = await Promise.all([
        scope(supabase
          .from('items')
          .select('id,name,category,unit,cost_per_unit')
          .eq('active', true)
          .order('name')),
        scope(supabase
          .from('suppliers')
          .select('id,name,contact')
          .eq('active', true)
          .order('name')),
        scope(supabase
          .from('receive_logs')
          .select('*')
          .gte('logged_at', startOfToday)
          .order('logged_at', { ascending: false })),
        // For AI anomaly detection — only the columns that drive the
        // analysis. Cap at 1000 rows to keep payload bounded.
        scope(supabase
          .from('receive_logs')
          .select('id, item_id, item_name, quantity, unit, cost_per_unit_at_log, supplier_name, logged_at')
          .gte('logged_at', since90Ts)
          .order('logged_at', { ascending: true })
          .limit(1000)),
      ])
      if (itemsRes.error)     throw itemsRes.error
      if (suppliersRes.error) throw suppliersRes.error
      if (logsRes.error)      throw logsRes.error
      // recentRes failure is non-fatal — anomaly scan just gets fewer rows

      setItems(itemsRes.data ?? [])
      setSuppliers(suppliersRes.data ?? [])
      setTodayLogs(logsRes.data ?? [])
      setRecentReceives(recentRes.data ?? [])
    } catch (e) {
      setError(e)
      // eslint-disable-next-line no-console
      console.error('[useReceiveData]', e)
    } finally {
      setLoading(false)
    }
  }, [currentRestaurantId])

  useEffect(() => { fetchAll() }, [fetchAll])

  // Auto-refresh at kitchen-day rollover. See useWasteData for rationale.
  useEffect(() => {
    let lastDate = new Date().toDateString()
    const id = setInterval(() => {
      const today = new Date().toDateString()
      if (today !== lastDate) {
        lastDate = today
        fetchAll()
      }
    }, 60_000)
    return () => clearInterval(id)
  }, [fetchAll])

  // Refresh when the tab becomes visible — same rationale as useWasteData.
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible') {
        fetchAll()
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [fetchAll])

  const todayTotal = useMemo(
    () => todayLogs.reduce((s, l) => s + Number(l.total_cost || 0), 0),
    [todayLogs]
  )

  // Distinct suppliers represented in today's deliveries — for the
  // small "X suppliers" summary line on the screen.
  const todaySupplierCount = useMemo(() => {
    const set = new Set()
    for (const l of todayLogs) {
      if (l.supplier_name) set.add(l.supplier_name)
    }
    return set.size
  }, [todayLogs])

  // Hard-delete a receive_log row. Confirmation happens at the call
  // site (in the screen component). After delete, refresh.
  const deleteLog = useCallback(async (log) => {
    const { error: delErr } = await supabase
      .from('receive_logs')
      .delete()
      .eq('id', log.id)
    if (delErr) throw delErr
    await fetchAll()
  }, [fetchAll])

  return {
    items,
    suppliers,
    todayLogs,
    todayTotal,
    todaySupplierCount,
    recentReceives,
    loading,
    error,
    refresh: fetchAll,
    deleteLog,
  }
}
